import { z } from 'zod';
import { idSchema } from './common';

/** Role an agent plays in the network. */
export const agentRoles = ['orchestrator', 'specialist', 'reviewer'] as const;
export const agentRoleSchema = z.enum(agentRoles);
export type AgentRole = z.infer<typeof agentRoleSchema>;

/** Directed relationship between two agents. */
export const relationTypes = ['orchestrates', 'reviews', 'reports_to'] as const;
export const relationTypeSchema = z.enum(relationTypes);
export type RelationType = z.infer<typeof relationTypeSchema>;

export const agentRelationSchema = z.object({
  tenantId: idSchema,
  parentAgentId: idSchema,
  childAgentId: idSchema,
  relationType: relationTypeSchema,
});
export type AgentRelation = z.infer<typeof agentRelationSchema>;

export const createRelationSchema = z.object({
  childAgentId: idSchema,
  relationType: relationTypeSchema.default('orchestrates'),
});
export type CreateRelation = z.infer<typeof createRelationSchema>;

/** Human reviewer roles attached to an agent stream. */
export const reviewerRoles = ['stream_owner', 'reviewer'] as const;
export const reviewerRoleSchema = z.enum(reviewerRoles);
export type ReviewerRole = z.infer<typeof reviewerRoleSchema>;

export const agentReviewerSchema = z.object({
  tenantId: idSchema,
  agentId: idSchema,
  userId: idSchema,
  role: reviewerRoleSchema.default('reviewer'),
});
export type AgentReviewer = z.infer<typeof agentReviewerSchema>;

export const createReviewerSchema = z.object({
  userId: idSchema,
  role: reviewerRoleSchema.default('reviewer'),
});
export type CreateReviewer = z.infer<typeof createReviewerSchema>;

/** A node in the workspace org diagram. */
export interface OrgNode {
  id: string;
  name: string;
  status: string;
  tier: number;
  agentRole: AgentRole;
  /** Outgoing edges (this agent → child) by relation type. */
  relations: { childAgentId: string; relationType: RelationType }[];
  reviewers: { userId: string; role: ReviewerRole; displayName?: string }[];
}

export interface WorkspaceOrg {
  workspaceId: string;
  tiers: { tier: number; agents: OrgNode[] }[];
}
